import type React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { EMAIL_REGEX, PHONE_NUMBER_REGEX } from "@/constants/misc";
import {
  isFieldMandatory,
  isFieldVisible,
} from "@/context/SalesPointProvider/actions";
import { selectContactInfo } from "@/store/contactInfo";
import { useAppSelector } from "@/store/hooks";
import { selectConfiguration } from "@/store/salesPoint";

type ContactDetailsFormProviderProps = {
  children?: React.ReactNode;
};

const ContactDetailsFormProvider: React.FC<ContactDetailsFormProviderProps> = ({
  children,
}) => {
  const contactInfo = useAppSelector(selectContactInfo);
  const configuration = useAppSelector(selectConfiguration);
  const { t } = useTranslation();

  const validationSchema = z.object({
    name: z
      .string()
      .nullable()
      .refine(
        (val) =>
          !isFieldMandatory("name", configuration) ||
          (val != null && val.length > 0),
        { message: t("form_error_message_required_field") },
      ),
    phone: z
      .string()
      .nullable()
      .refine(
        (val) =>
          !isFieldVisible("phone", configuration) ||
          !val ||
          PHONE_NUMBER_REGEX.test(val),
        { message: t("contact_details_enter_valid_phone_number") },
      )
      .refine(
        (val) =>
          !isFieldMandatory("phone", configuration) ||
          (val != null && val.length > 0),
        { message: t("form_error_message_required_field") },
      ),
    email: z
      .string()
      .nullable()
      .refine(
        (val) =>
          !isFieldVisible("email", configuration) ||
          !val ||
          EMAIL_REGEX.test(val),
        { message: t("contact_details_enter_valid_email") },
      )
      .refine(
        (val) =>
          !isFieldMandatory("email", configuration) ||
          (val != null && val.length > 0),
        { message: t("form_error_message_required_field") },
      ),
  });

  type Validation = z.infer<typeof validationSchema>;

  const form = useForm<Validation>({
    values: {
      name: contactInfo.Name || null,
      phone: contactInfo.Phone || null,
      email: contactInfo.Email || null,
    },
    mode: "onBlur",
    resolver: zodResolver(validationSchema),
  });

  return <FormProvider {...form}>{children}</FormProvider>;
};

export default ContactDetailsFormProvider;
